import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { CollegePage, DashboardSettings, Student } from '../../models/college-dashboard.model';
import { CollegeDashboardService } from '../../services/college-dashboard.service';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { CollegePortalService } from '../../../college-workspace/services/college-portal.service';
import { CollegeProfile } from '../../../college-workspace/models/college-portal.models';
import { CollegeSidebarComponent } from '../../components/sidebar/college-sidebar.component';
import { CollegeHeaderComponent } from '../../components/header/college-header.component';
import { StudentProfileModalComponent } from '../../components/modals/student-profile-modal/student-profile-modal.component';
import { JobDetailModalComponent } from '../../components/modals/job-detail-modal/job-detail-modal.component';
import { ReadinessProfileModalComponent } from '../../components/modals/readiness-profile-modal/readiness-profile-modal.component';
// OfficerProfileModalComponent replaced by a navigation to the real /college-profile page — see onOpenProfile().
import { AttentionDetailModalComponent } from '../../components/modals/attention-detail-modal/attention-detail-modal.component';
import { CollegeOverviewComponent } from '../overview/college-overview.component';
import { CollegeStudentsComponent } from '../students/college-students.component';
import { CollegeJobsComponent } from '../companies-jobs/college-jobs.component';
import { CollegeReadinessComponent } from '../readiness/college-readiness.component';
import { CollegeNotificationsComponent } from '../notifications/college-notifications.component';
import { CollegeSettingsComponent } from '../settings/college-settings.component';
import { CollegeProfileComponent } from '../../../college-workspace/college-profile/college-profile.component';

@Component({
  selector: 'app-college-dashboard-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CollegeSidebarComponent,
    CollegeHeaderComponent,
    StudentProfileModalComponent,
    JobDetailModalComponent,
    ReadinessProfileModalComponent,
    AttentionDetailModalComponent,
    CollegeOverviewComponent,
    CollegeStudentsComponent,
    CollegeJobsComponent,
    CollegeReadinessComponent,
    CollegeNotificationsComponent,
    CollegeSettingsComponent,
    CollegeProfileComponent
  ],
  templateUrl: './college-dashboard-layout.component.html',
  styleUrl: './college-dashboard-layout.component.scss'
})
export class CollegeDashboardLayoutComponent implements OnInit, OnDestroy {
  activePage: CollegePage = 'Overview';
  settings: DashboardSettings = {
    academicYear: 'All Years',
    department: 'All Departments',
    institutionName: 'P.K. College of Engineering',
    placementOfficer: 'Placement Officer',
    contactEmail: 'placements@pkce.edu',
    weeklyEmails: true,
    opportunityAlerts: true,
    practiceCredits: false
  };
  unreadNotificationsCount: number = 0;
  toastMessage: string = '';
  isSidebarCollapsed: boolean = false;

  // Modals state
  selectedStudent: Student | null = null;
  selectedStudentUserId: string | null = null;
  selectedJobId: string | null = null;
  readinessStudent: Student | null = null;
  isAttentionModalOpen: boolean = false;
  activeAttentionCategory: 'Profiles Incomplete' | 'Offers Awaiting Response' | 'New Companies This Week' = 'Profiles Incomplete';

  // Real college profile fields (college name, admin/POC name, admin email)
  // override the mocked dashboard settings once fetched — see fetchCollegeProfile().
  private profileOverride: Partial<Pick<DashboardSettings, 'institutionName' | 'placementOfficer' | 'contactEmail'>> = {};
  collegeLogoUrl: string | null = null;
  deptOptions: string[] = ['All Departments'];
  // Distinct years_of_passing actually present among this college's students
  // (fetched once here, passed down to both the header and Settings so
  // "Default academic year" offers the same options) — ensures a real
  // outlier (e.g. year_of_passing 2010 or 2050) is always filterable.
  // Empty until fetchAvailableYears() resolves or for a college with zero
  // students yet, in which case the year dropdown is just ['All Years'] —
  // no guessed/padded years, by design (see college-header.component.ts).
  availableYears: number[] = [];

  private subs = new Subscription();
  private toastTimer: any = null;

  constructor(
    private dashboardService: CollegeDashboardService,
    private router: Router,
    private route: ActivatedRoute,
    private _authService: AuthServiceService,
    private _collegePortalService: CollegePortalService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.dashboardService.settings$.subscribe((s) => {
        this.settings = { ...s, ...this.profileOverride };
      })
    );

    this.fetchCollegeProfile();
    this.fetchAvailableYears();

    this.subs.add(
      this.dashboardService.notifications$.subscribe((items) => {
        this.unreadNotificationsCount = items.filter((n) => n.unread).length;
      })
    );

    this.subs.add(
      this.dashboardService.toast$.subscribe((msg) => {
        this.toastMessage = msg;
        if (this.toastTimer) clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => {
          this.toastMessage = '';
        }, 2400);
      })
    );

    this.subs.add(
      this.dashboardService.activePage$.subscribe((page) => {
        this.activePage = page;
      })
    );

    // Sync route with active page
    this.updatePageFromUrl(this.router.url);

    this.subs.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: any) => {
          this.updatePageFromUrl(event.urlAfterRedirects || event.url);
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  private updatePageFromUrl(url: string): void {
    if (url.includes('/students')) {
      this.activePage = 'Students';
    } else if (url.includes('/companies-jobs') || url.includes('/jobs')) {
      this.activePage = 'Companies & Jobs';
    } else if (url.includes('/readiness')) {
      this.activePage = 'Placement Readiness';
    } else if (url.includes('/notifications')) {
      this.activePage = 'Notifications';
    } else if (url.includes('/settings')) {
      this.activePage = 'Settings';
    } else if (url.includes('/college-profile')) {
      this.activePage = 'Profile';
    } else {
      this.activePage = 'Overview';
    }
  }

  onPageChange(page: CollegePage): void {
    this.activePage = page;
    this.dashboardService.setActivePage(page);
    this.selectedStudent = null;
    this.selectedJobId = null;
    this.readinessStudent = null;

    // Navigate to appropriate subroute
    let routeSegment = 'overview';
    switch (page) {
      case 'Students':
        routeSegment = 'students';
        break;
      case 'Companies & Jobs':
        routeSegment = 'companies-jobs';
        break;
      case 'Placement Readiness':
        routeSegment = 'readiness';
        break;
      case 'Notifications':
        routeSegment = 'notifications';
        break;
      case 'Settings':
        routeSegment = 'settings';
        break;
    }
    this.router.navigate(['/college-dashboard', routeSegment]);
  }

  onYearChange(year: string): void {
    this.dashboardService.setAcademicYear(year);
  }

  onDeptChange(dept: string): void {
    this.dashboardService.setDepartment(dept);
  }

  onSelectStudent(student: Student): void {
    this.selectedStudent = student;
  }

  onViewStudentByUserId(studentUserId: string): void {
    this.selectedStudentUserId = studentUserId;
  }

  onSelectJob(jobId: string): void {
    this.selectedJobId = jobId;
  }

  onViewReadiness(student: Student): void {
    this.selectedStudent = null;
    this.readinessStudent = student;
  }

  onOpenAttention(category: 'Profiles Incomplete' | 'Offers Awaiting Response' | 'New Companies This Week'): void {
    this.activeAttentionCategory = category;
    this.isAttentionModalOpen = true;
  }

  onOpenProfile(): void {
    this.router.navigate(['/college-dashboard/college-profile']);
  }

  get effectiveSidebarCollapsed(): boolean {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      return false;
    }
    return this.isSidebarCollapsed;
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  onSignOut(): void {
    this.subs.add(
      this._authService.logout(this.router).subscribe()
    );
  }

  onProfileSaved(profile: CollegeProfile): void {
    this.applyProfile(profile);
  }

  private fetchCollegeProfile(): void {
    this.subs.add(
      this._collegePortalService.getCollegeProfile().subscribe({
        next: (response) => {
          if (response?.Success && response.Data) {
            this.applyProfile(response.Data);
          }
        },
        error: () => {
          // Keep mocked defaults if the profile can't be fetched.
        }
      })
    );
  }

  private fetchAvailableYears(): void {
    this.subs.add(
      this._collegePortalService.getStudentYears().subscribe({
        next: (response) => {
          if (response?.Success && response.Data) {
            this.availableYears = response.Data.years;
          }
        },
        error: () => {
          // Keep availableYears empty — year dropdown just shows 'All Years'.
        }
      })
    );
  }

  private applyProfile(profile: CollegeProfile): void {
    this.profileOverride = {
      institutionName: profile.college_name,
      placementOfficer: profile.point_of_contact_name,
      contactEmail: profile.point_of_contact_email
    };
    this.settings = { ...this.settings, ...this.profileOverride };
    this.collegeLogoUrl = profile.college_logo_url || null;
    const combined = Array.from(new Set([...(profile.streams || []), ...(profile.courses_offered || [])]));
    this.deptOptions = ['All Departments', ...combined];
  }
}
